<script setup lang="ts">
import { computed, ref } from "vue";
import { Badge, Card, FuelTimeline, ProgressBar, ProgressRing, StatTile, Tabs } from "@/design-system";
import { fmtTime, K } from "@/domain/calc";
import { KIND_ICON } from "@/domain/catalogue";
import type { TimelineItem } from "@/design-system";
import type { Plan } from "@/composables/usePlan";

/** Step 3c -- the per-leg result (FR-3.3 to FR-3.5). */
const props = defineProps<{ plan: Plan }>();
const p = props.plan;

const view = ref<"numbers" | "schedule">("numbers");
const r1 = (n: number) => Math.round(n * 10) / 10;

const res = computed(() => p.activeResult.value.res);
const isSwim = computed(() => p.activeResult.value.type === "swim");
const isRun = computed(() => p.activeResult.value.type === "run");

const ringMax = computed(() => p.weight.value * 1.3);
const ringValue = computed(() =>
  isSwim.value ? ringMax.value : Math.min(res.value.perHour.avg, ringMax.value),
);

const tabs = computed(() => [
  { value: "numbers", label: p.t.value.resultTitle },
  { value: "schedule", label: p.t.value.schedule },
]);

const timeline = computed<TimelineItem[]>(() =>
  p.activeSchedule.value.map((entry, i) => ({
    time: entry.preStart ? p.t.value.preShort : fmtTime(entry.minute),
    icon: KIND_ICON[entry.kind],
    // A drink mark carries no product name and no detail -- omit the key
    // rather than passing undefined (exactOptionalPropertyTypes).
    title: entry.name ?? p.t.value.sips,
    ...(entry.detail ? { detail: entry.detail } : {}),
    state: i === 0 ? ("next" as const) : ("upcoming" as const),
  })),
);
</script>

<template>
  <div class="result">
    <Card
      variant="brand"
      data-testid="result-card"
    >
      <div class="result__ring">
        <p class="cw-eyebrow result__eyebrow">
          {{ p.activeResult.value.label.stage }} · {{ p.t.value.perHour }}
        </p>
        <ProgressRing
          :value="ringValue"
          :max="ringMax"
          :size="196"
          :thickness="17"
          track="#224037"
          :color="isSwim ? 'var(--fuel-fluid)' : 'var(--fuel-carb)'"
        >
          <span
            class="result__value"
            data-testid="ring-value"
          >{{ isSwim ? r1(res.totalCarbs) : r1(res.perHour.avg) }}</span>
          <span class="result__unit">{{ isSwim ? "g" : "g/h" }}</span>
        </ProgressRing>
        <Badge :tone="p.activeVerdict.value.tone">
          {{ isSwim ? p.t.value.swimPre : p.activeVerdict.value.label }}
        </Badge>
        <p class="result__ceiling">
          {{ p.t.value.ceiling }}: {{ p.ceiling.value.min }}–{{ p.ceiling.value.max }} g/h
        </p>
        <p
          v-if="!isSwim"
          class="result__span"
        >
          {{ r1(res.perHour.min) }} – {{ r1(res.perHour.max) }} g/h ·
          {{ fmtTime(res.hours.min * 60) }} – {{ fmtTime(res.hours.max * 60) }} h
        </p>
      </div>
    </Card>

    <Tabs
      v-model="view"
      :options="tabs"
      data-testid="result-tabs"
    />

    <template v-if="view === 'numbers'">
      <div class="result__tiles">
        <StatTile
          :label="p.t.value.total"
          :value="r1(res.totalCarbs)"
          unit="g"
          size="sm"
        />
        <StatTile
          :label="p.t.value.kcal"
          :value="Math.round(res.totalKcal)"
          variant="sunken"
          size="sm"
        />
        <StatTile
          v-if="!isSwim"
          :label="isRun ? p.t.value.cups : p.t.value.bottles"
          :value="res.vesselsPerRace === null ? '—' : Math.ceil(res.vesselsPerRace)"
          variant="sunken"
          size="sm"
        />
        <StatTile
          :label="p.t.value.items"
          :value="res.itemCount"
          size="sm"
        />
      </div>

      <Card v-if="!isSwim">
        <ProgressBar
          :value="res.perHour.avg"
          :max="p.weight.value * 1.3"
          color="var(--fuel-carb)"
          :label="p.t.value.perHour"
          :caption="`${r1(res.perHour.avg)} / ${p.ceiling.value.max} g`"
        />
        <div class="result__bar-gap">
          <ProgressBar
            :value="res.fluidPerHour"
            :max="K.fluidMax"
            color="var(--fuel-fluid)"
            :label="p.t.value.fluid"
            :caption="`${res.fluidPerHour} ml/h`"
          />
        </div>
        <dl class="result__kv">
          <div v-if="res.eatEveryMin !== null">
            <dt>{{ p.t.value.eatEvery }}</dt>
            <dd>{{ Math.round(res.eatEveryMin) }} {{ p.t.value.minutes }}</dd>
          </div>
          <div v-if="res.anyEveryMin !== null">
            <dt>{{ p.t.value.drinkEvery }}</dt>
            <dd>{{ Math.round(res.anyEveryMin) }} {{ p.t.value.minutes }}</dd>
          </div>
          <div v-if="isRun && res.cupsPerStation !== null">
            <dt>{{ p.t.value.perStation }}</dt>
            <dd>{{ r1(res.cupsPerStation) }}</dd>
          </div>
        </dl>
      </Card>
    </template>

    <Card
      v-else
      data-testid="schedule-card"
    >
      <FuelTimeline
        v-if="timeline.length"
        :items="timeline"
      />
      <p
        v-else
        class="result__empty"
      >
        {{ p.t.value.emptyProducts }}
      </p>
    </Card>

    <p class="result__disclaimer">
      {{ isSwim ? p.t.value.swimDisclaimer : p.t.value.disclaimer }}
    </p>
  </div>
</template>

<style scoped>
.result { display: flex; flex-direction: column; gap: var(--space-6); }
.result__ring {
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: var(--space-5);
  padding: var(--space-4) 0;
  text-align: center;
}
.result__eyebrow { margin: 0; color: var(--pine-200); }
.result__value {
  font-family: var(--font-mono);
  font-size: 52px;
  font-weight: var(--weight-bold);
  color: var(--sand-50);
  font-variant-numeric: tabular-nums;
  line-height: 1;
}
.result__unit {
  font: var(--type-unit);
  color: var(--pine-200);
  text-transform: uppercase;
  letter-spacing: var(--tracking-label);
}
.result__ceiling,
.result__span { margin: 0; color: var(--pine-100); font: var(--type-body-sm); }
.result__span {
  font-family: var(--font-mono);
  text-transform: uppercase;
  letter-spacing: var(--tracking-label);
  font-variant-numeric: tabular-nums;
}
.result__tiles {
  display: grid;
  grid-template-columns: repeat(2, minmax(0, 1fr));
  gap: var(--space-5);
}
.result__bar-gap { margin-top: var(--space-6); }
.result__kv {
  margin: var(--space-6) 0 0;
  padding-top: var(--space-6);
  border-top: 1px solid var(--border-subtle);
  display: flex;
  flex-direction: column;
  gap: var(--space-4);
}
.result__kv > div {
  display: flex;
  justify-content: space-between;
  gap: var(--space-5);
}
.result__kv dt { font: var(--type-body-sm); color: var(--text-muted); }
.result__kv dd {
  margin: 0;
  font-family: var(--font-mono);
  font-variant-numeric: tabular-nums;
  color: var(--text-strong);
}
.result__empty { margin: 0; font: var(--type-body-sm); color: var(--text-muted); }
.result__disclaimer { margin: 0; font: var(--type-body-sm); color: var(--text-muted); }
</style>
